import { inject, injectable } from "inversify";
import type * as THREE from "three";
import { EventDispatcher, type IService, Logger, type PointerPosition } from "@designboticteam/core";
import { CursorSnappingService } from "../../../input/CursorSnappingService";
import { isHighlightable, isSelectable } from "../../../layers";
import { Workspace3DStoreService } from "../../../services/Workspace3DStoreService";
import { CAMERA_CONTROL_SERVICE, type ICameraControlService } from "../../../stage/CameraControlService";
import type { Shape } from "../../entities/_base/Shape";
import { EdgeEditHandler } from "../../tools/modify/EdgeEditHandler";
import type { IShapeEditHandler } from "../../tools/modify/IShapeEditHandler";
import { VertexEditHandler } from "../../tools/modify/VertexEditHandler";

/**
 * Shape selection + editing, shared by every tool: the registry of selectable shapes, the selected /
 * highlighted shape in the store, and the vertex/edge edit handlers. Not a manager-activated ITool —
 * tools drive it directly (ZoneTool while editing, SelectTool always) by forwarding pointer events.
 */
@injectable()
class ShapeEditingService implements IService {
	public readonly serviceName = "ShapeEditingService";
	private readonly logger = new Logger(`✏️ ${this.serviceName}`, { color: "#ffffff", backgroundColor: "#0f766e" });

	public readonly onShapeEdited = new EventDispatcher<{ shape: Shape }>("ShapeEditingService.onShapeEdited");

	protected registeredShapes: Map<THREE.Object3D, Shape> = new Map();
	private selectedShape: Shape | null = null;

	public registerShape(shape: Shape) {
		shape.sceneObject.traverse((sceneObject) => {
			this.registeredShapes.set(sceneObject, shape);
		});
	}

	constructor(
		@inject(CursorSnappingService) private cursorSnappingService: CursorSnappingService,
		@inject(CAMERA_CONTROL_SERVICE) private cameraControlService: ICameraControlService,
		@inject(Workspace3DStoreService) private workspace3DStoreService: Workspace3DStoreService,
		@inject(VertexEditHandler) private vertexEditHandler: IShapeEditHandler,
		@inject(EdgeEditHandler) private edgeEditHandler: EdgeEditHandler,
	) {
		this.logger.log("constructed");
	}

	public initialize(): void {
		this.logger.log("initialized");
	}

	public dispose(): void {
		this.reset();
		this.onShapeEdited.clear();
		this.logger.log("disposed");
	}

	/** Drops the hover and the selection (and with it the edit targets). */
	public reset(): void {
		this.highlightShape(null);
		this.selectShape(null);
	}

	public setEdgeOffset({ distance }: { distance: number }): void {
		// The handler reports whether the geometry actually moved; a rejected offset must not trigger revalidation.
		if (this.edgeEditHandler.setOffset({ distance }) && this.selectedShape !== null) {
			this.onShapeEdited.dispatch({ shape: this.selectedShape });
		}
	}

	public clearEdgeSelection(): void {
		this.edgeEditHandler.clearSelection();
	}

	public selectAllEdges(): void {
		this.edgeEditHandler.selectAllEdges();
	}

	/**
	 * @description Removes object from shapes possible to select
	 */
	public unregisterShape(shape: Shape) {
		shape.sceneObject.traverse((object) => {
			this.registeredShapes.delete(object);
		});

		this.selectedShape = null;
		// Without this the edit handlers keep pointing at a shape that is about to be disposed.
		this.setEditTarget(null);
		this.workspace3DStoreService.store.setState((state) => ({ tools: { ...state.tools, highlightedShape: null, selectedShape: null } }));
	}

	/**
	 * Unregister all shapes
	 */
	public clearAll() {
		this.registeredShapes.clear();
		this.selectedShape = null;
		this.setEditTarget(null);
		this.workspace3DStoreService.store.setState((state) => ({ tools: { ...state.tools, highlightedShape: null, selectedShape: null } }));
	}

	/** */
	public onPointerMove(_position: PointerPosition): void {
		if (this.isEditingShape() === false) {
			this.vertexEditHandler.onPointerMove?.();
			this.edgeEditHandler.onPointerMove?.();
		}

		const target = this.cursorSnappingService.pickSceneObject();
		this.highlightShape(target !== null && isHighlightable(target) ? this.resolveShape(target) : null);
	}

	private highlightShape(shape: Shape | null): void {
		if (shape === this.workspace3DStoreService.store.getState().tools.highlightedShape) {
			return;
		}

		this.workspace3DStoreService.store.setState((state) => ({ tools: { ...state.tools, highlightedShape: shape } }));
	}

	private resolveShape(sceneObject: THREE.Object3D): Shape | null {
		return this.registeredShapes.get(sceneObject) ?? null;
	}

	/** */
	public onTap(_position: PointerPosition): void {
		if (this.edgeEditHandler.onTap?.() === true) {
			return;
		}

		const target = this.cursorSnappingService.pickSceneObject();

		if (target === null) {
			this.selectObject(null);
			return;
		}

		this.selectObject(target);
	}

	public selectObject(sceneObject: THREE.Object3D | null) {
		this.selectShape(sceneObject !== null && isSelectable(sceneObject) ? this.resolveShape(sceneObject) : null);
	}

	public selectShape(shape: Shape | null) {
		this.selectedShape?.deselect();
		this.selectedShape = shape;
		shape?.select();
		this.setEditTarget(shape);
		this.workspace3DStoreService.store.setState((state) => ({ tools: { ...state.tools, selectedShape: shape } }));
	}

	private setEditTarget(shape: Shape | null) {
		this.vertexEditHandler.setTarget(shape);
		this.edgeEditHandler.setTarget(shape);
	}

	// Fires on the press itself, before the drag threshold has moved the cursor — this is where the
	// handlers decide what a drag would grab, so grab and hover always agree.
	public onPointerDown(_pointerPosition: PointerPosition): void {
		this.vertexEditHandler.onPointerDown?.();
		this.edgeEditHandler.onPointerDown?.();
	}

	public onDragStart(_pointerPosition: PointerPosition): void {
		this.vertexEditHandler.onDragStart();

		// A vertex grab wins over an edge grab, so the edge handler only gets a chance when the
		// drag did not start on a vertex.
		if (this.vertexEditHandler.isEngaged() === false) {
			this.edgeEditHandler.onDragStart();
		}

		if (this.isEditingShape()) {
			this.cameraControlService.disable();
		}
	}

	public onDragMove(_pointerPosition: PointerPosition): void {
		this.vertexEditHandler.onDragMove();
		this.edgeEditHandler.onDragMove();
	}

	public onDragEnd(_pointerPosition: PointerPosition): void {
		const wasEditingShape = this.isEditingShape();

		this.vertexEditHandler.onDragEnd();
		this.edgeEditHandler.onDragEnd();

		if (wasEditingShape) {
			this.cameraControlService.enable();

			if (this.selectedShape !== null) {
				this.onShapeEdited.dispatch({ shape: this.selectedShape });
			}
		}
	}

	private isEditingShape(): boolean {
		return this.vertexEditHandler.isEngaged() || this.edgeEditHandler.isEngaged();
	}
}

export { ShapeEditingService };
